import { ReactNode, useEffect, useRef, useState } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { extractErrorMessage } from '@/lib/api';

export interface InlineEditFieldProps<T> {
  label: string;
  /** Current value. null/undefined renders as "— Click to set". */
  value: T | null | undefined;
  /** How to render the value in display mode. If omitted, value is stringified. */
  renderValue?: (v: T) => ReactNode;
  /** Optional read-only fallback when canEdit is false. */
  renderReadOnly?: (v: T | null | undefined) => ReactNode;
  /** The input component to render in edit mode. Receives controlled value + onChange. */
  editor: (args: { value: T | null; onChange: (v: T | null) => void; onCommit: () => void; onCancel: () => void }) => ReactNode;
  /** Async save handler. Throw or reject to surface the error to the user. */
  onSave: (newValue: T | null) => Promise<void> | void;
  /** Defaults true. When false, the row is read-only (no pencil, no click-to-edit). */
  canEdit?: boolean;
  /** Label for empty/null. Default "— Click to set" (or "— Read only" when canEdit=false). */
  emptyLabel?: string;
  /** Inline / row layout. Defaults to row (label on left, value on right). */
  layout?: 'row' | 'stacked';
}

export function InlineEditField<T>({
  label,
  value,
  renderValue,
  renderReadOnly,
  editor,
  onSave,
  canEdit = true,
  emptyLabel,
  layout = 'row',
}: InlineEditFieldProps<T>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<T | null>(value ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setDraft(value ?? null); }, [value]);

  const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

  const fallback = emptyLabel ?? (canEdit ? '— Click to set' : '—');
  const displayed: ReactNode = isEmpty
    ? <span className="text-muted-foreground italic">{fallback}</span>
    : (renderValue ? renderValue(value as T) : String(value));

  if (!canEdit && renderReadOnly) {
    return (
      <RowShell label={label} layout={layout}>
        {renderReadOnly(value)}
      </RowShell>
    );
  }

  async function commit() {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value ?? null);
    setEditing(false);
    setError(null);
  }

  if (!editing) {
    return (
      <RowShell label={label} layout={layout}>
        <button
          type="button"
          onClick={() => canEdit && setEditing(true)}
          className={'group inline-flex items-center gap-2 text-left ' + (canEdit ? 'cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1' : 'cursor-default')}
        >
          <span className="min-w-0">{displayed}</span>
          {canEdit && <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />}
        </button>
      </RowShell>
    );
  }

  return (
    <RowShell label={label} layout={layout}>
      <div ref={rootRef} className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            {editor({
              value: draft,
              onChange: setDraft,
              onCommit: () => void commit(),
              onCancel: cancel,
            })}
          </div>
          <Button size="sm" variant="default" onClick={commit} disabled={saving} className="gap-1">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={cancel} disabled={saving} className="gap-1">
            <X className="h-3 w-3" /> Cancel
          </Button>
        </div>
        {error && <div className="text-xs text-rose-700 dark:text-rose-400">{error}</div>}
      </div>
    </RowShell>
  );
}

function RowShell({ label, layout, children }: { label: string; layout: 'row' | 'stacked'; children: ReactNode }) {
  if (layout === 'stacked') {
    return (
      <div className="space-y-1">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div>{children}</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start py-1.5 border-b border-border last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-muted-foreground pt-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

// Convenience editors

export function TextEditor({ value, onChange, onCommit, onCancel, placeholder }: {
  value: string | null;
  onChange: (v: string | null) => void;
  onCommit: () => void;
  onCancel: () => void;
  placeholder?: string;
}) {
  return (
    <input
      autoFocus
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      onKeyDown={(e) => { if (e.key === 'Enter') onCommit(); else if (e.key === 'Escape') onCancel(); }}
      placeholder={placeholder}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
    />
  );
}

export function NumberEditor({ value, onChange, onCommit, onCancel, min, step }: {
  value: number | null;
  onChange: (v: number | null) => void;
  onCommit: () => void;
  onCancel: () => void;
  min?: number;
  step?: number;
}) {
  return (
    <input
      autoFocus
      type="number"
      min={min ?? 0}
      step={step ?? 1}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      onKeyDown={(e) => { if (e.key === 'Enter') onCommit(); else if (e.key === 'Escape') onCancel(); }}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
    />
  );
}

export function SelectEditor({ value, onChange, options }: {
  value: string | null;
  onChange: (v: string | null) => void;
  onCommit: () => void;
  onCancel: () => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      autoFocus
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
    >
      <option value="">— None —</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function DateTimeEditor({ value, onChange, onCommit, onCancel }: {
  value: string | null;
  onChange: (v: string | null) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  // Normalize to YYYY-MM-DDThh:mm for input type=datetime-local
  const localVal = value ? value.slice(0, 16) : '';
  return (
    <input
      autoFocus
      type="datetime-local"
      value={localVal}
      onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
      onKeyDown={(e) => { if (e.key === 'Enter') onCommit(); else if (e.key === 'Escape') onCancel(); }}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
    />
  );
}
